import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppWindow, Laptop } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import { indentedName } from '../lib/automation';
import { useDesk } from '../lib/desk';
import { equipmentLabel, useEquipmentOptions } from '../lib/facilities';
import { personName } from '../lib/format';
import { invalidateInventory, useDebouncedValue } from '../lib/hooks';
import { useOfferedSoftware } from '../lib/software';
import { useRequestForm, type RequestFormField } from '../lib/ticket-images';
import type { DepartmentOption, MyDevices, PersonSummary, ReportableAsset, WorkOrder, WorkOrderCategory, WorkOrderPriority } from '../lib/types';
import { WORK_ORDER_CATEGORY_LABEL } from '../lib/work-orders';
import { PlaceFields } from './facilities-ui';
import { RelatedArticles } from './knowledge';
import { PicturesField, usePendingPictures } from './ticket-images';
import { Combobox, PersonPicker } from './pickers';
import { ModelImage } from './model-image';
import { useToast } from './toast';
import { Alert, Button, cn, Field, Input, Modal, Segmented, Select, Textarea } from './ui';

interface DeviceRef {
  id: string;
  assetTag: string;
  name: string;
  /** Its model in Makes & models and that model's name, for the help articles tagged with it. */
  modelId?: string | null;
  model?: string | null;
}

/** Facilities equipment a request is about, e.g. when reporting a problem from the equipment's page. */
export interface EquipmentPreset {
  id: string;
  code: string | null;
  name: string;
  buildingId: string | null;
  roomId: string | null;
}

interface WorkOrderFormModalProps {
  open: boolean;
  onClose(): void;
  /** Technology help desk. Requesters: preselected in the dropdown. Staff: the fixed device. */
  device?: DeviceRef | null;
  /** Facilities: the fixed equipment, which also fills in the building and room. */
  equipment?: EquipmentPreset | null;
  staff?: boolean;
  onCreated?(workOrder: WorkOrder): void;
}

/** What a technology ticket is about. */
type Subject = 'device' | 'software';

/** Dropdown value for "a device that isn't listed", identified by its asset tag. */
const OTHER_DEVICE = '__other__';
/** Staff only: work that isn't about a device. */
const NO_DEVICE = '__none__';

/** Hidden from requesters until the school's form settings load, so fields don't flash up and vanish. */
const ALL_FIELDS: RequestFormField[] = ['DEPARTMENT', 'PRIORITY', 'CATEGORY'];

const PRIORITY_OPTIONS: { value: WorkOrderPriority; label: string }[] = [
  { value: 'LOW', label: 'Low - whenever you get a chance' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HIGH', label: 'High - it’s getting in the way of teaching or work' },
  { value: 'URGENT', label: 'Urgent - class or work can’t continue' },
];

const FACILITIES_PRIORITY_OPTIONS: { value: WorkOrderPriority; label: string }[] = [
  { value: 'LOW', label: 'Low - whenever there’s time' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HIGH', label: 'High - it’s getting in the way of classes or work' },
  { value: 'URGENT', label: 'Urgent - it isn’t safe, or a space can’t be used' },
];

/** Dropdown groups: the person's own devices first, then each room. */
function groupDevices(devices: ReportableAsset[], ownLabel: string) {
  const groups = new Map<string, ReportableAsset[]>();
  for (const device of devices) {
    const label =
      device.reason === 'ASSIGNED_TO_YOU'
        ? ownLabel
        : device.reason === 'ASSIGNED_TO_ROOM'
          ? `Assigned to ${device.roomName ?? 'a room'}`
          : `Kept in ${device.roomName ?? 'a room'}`;
    groups.set(label, [...(groups.get(label) ?? []), device]);
  }
  return [...groups].sort(([a], [b]) => (a === ownLabel ? -1 : b === ownLabel ? 1 : a.localeCompare(b)));
}

/** The device picked in the form, so people can check it's the right one. */
function DevicePreview({ device }: { device: ReportableAsset }) {
  const rows: [string, string][] = [
    ['Name', device.name],
    ['Asset tag', device.assetTag],
    ...(device.model ? [['Model', device.model] as [string, string]] : []),
    ['Location', device.location ?? 'Not recorded'],
    ['Assigned to', device.assignedTo ?? 'Nobody'],
  ];
  return (
    <div className="flex gap-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3" aria-label="Selected device">
      <ModelImage url={device.imageUrl} alt={device.model ?? ''} className="size-24" />
      <div className="min-w-0 flex-1">
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-sm">
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-slate-500">{label}</dt>
              <dd className={label === 'Asset tag' ? 'truncate font-mono text-slate-900' : 'truncate text-slate-900'}>{value}</dd>
            </div>
          ))}
        </dl>
        {device.workOrders.length > 0 && (
          <p className="mt-1.5 text-xs text-amber-700">
            Already reported: #{device.workOrders[0].number} {device.workOrders[0].title}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * New tickets on the page's help desk.
 *
 * Technology help desk. Requesters: "Report a problem", choosing from their own devices or entering another device's asset
 * tag, or software from the school's list, and can add screenshots or photos. Administrators can hide the department,
 * urgency, and problem type from requesters. Staff (`staff`): every field shows, the device list follows whoever is
 * picked under "Reported by", and work doesn't need a device. Opened from an asset's page (`staff` + `device`), the
 * device is fixed.
 *
 * Facilities: where the work is needed (a building is required), what kind of work it is, and the equipment when someone
 * knows it. Routing rules assign it.
 */
export function WorkOrderFormModal(props: WorkOrderFormModalProps) {
  const desk = useDesk();
  return desk.facilities ? <FacilitiesRequestModal {...props} /> : <TechnologyTicketModal {...props} />;
}

function TechnologyTicketModal({ open, onClose, device, staff = false, onCreated }: WorkOrderFormModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const fixedDevice = staff ? (device ?? null) : null;

  const [requester, setRequester] = useState<PersonSummary | null>(null);
  const [subject, setSubject] = useState<Subject>('device');
  const [selection, setSelection] = useState('');
  const [softwareId, setSoftwareId] = useState('');
  const [assetTag, setAssetTag] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<WorkOrderCategory>('OTHER');
  const [softwareProblemTypeId, setSoftwareProblemTypeId] = useState('');
  const [priority, setPriority] = useState<WorkOrderPriority>('NORMAL');
  const [error, setError] = useState('');
  const pictures = usePendingPictures();
  const resetPictures = pictures.reset;

  useEffect(() => {
    if (!open) return;
    resetPictures();
    setRequester(null);
    setSubject('device');
    setSelection(!staff && device ? device.id : '');
    setSoftwareId('');
    setAssetTag('');
    setDepartmentId('');
    setTitle('');
    setDescription('');
    setCategory('OTHER');
    setSoftwareProblemTypeId('');
    setPriority('NORMAL');
    setError('');
  }, [open, device, staff, resetPictures]);

  const listsDevices = !fixedDevice && (!staff || !!requester);
  const options = useQuery({
    queryKey: staff ? ['work-orders', 'device-options', requester?.id] : ['my-devices'],
    queryFn: () =>
      staff
        ? api.get<MyDevices>('/work-orders/device-options', { personId: requester!.id })
        : api.get<MyDevices>('/my/devices'),
    enabled: open && listsDevices,
  });
  const software = useOfferedSoftware(open && !fixedDevice);
  const softwareOptions = software.data ?? [];
  // Software is a choice once the school has listed some.
  const offersSoftware = !fixedDevice && softwareOptions.length > 0;
  const aboutSoftware = offersSoftware && subject === 'software';
  const selectedSoftware = aboutSoftware ? (softwareOptions.find((entry) => entry.id === softwareId) ?? null) : null;
  // Software with its own list of problems is reported against that; the rest use the device categories.
  const softwareProblems = selectedSoftware?.problemTypes ?? [];
  const ownProblems = softwareProblems.length > 0;
  const departments = useQuery({
    queryKey: ['departments', 'selectable', 'IT'],
    queryFn: () => api.get<DepartmentOption[]>('/my/departments'),
    enabled: open,
    staleTime: 60_000,
  });
  const requestForm = useRequestForm(open && !staff);
  const hidden = staff ? [] : (requestForm.data?.hiddenFields ?? ALL_FIELDS);
  const showCategory = !hidden.includes('CATEGORY');
  const showPriority = !hidden.includes('PRIORITY');
  const showDepartment = !hidden.includes('DEPARTMENT') && !!departments.data?.length;

  const devices = listsDevices ? (options.data?.assets ?? []) : [];
  const loadingDevices = listsDevices && options.isFetching && devices.length === 0;
  const listedIds = new Set(devices.map((d) => d.id));
  // Requesters with nothing to choose from can only enter an asset tag.
  const selected =
    selection === OTHER_DEVICE || (staff && selection === NO_DEVICE) || listedIds.has(selection)
      ? selection
      : !staff && !loadingDevices && devices.length === 0
        ? OTHER_DEVICE
        : '';
  const ownLabel = staff && requester ? `Assigned to ${personName(requester)}` : 'Assigned to you';
  const selectedDevice = devices.find((d) => d.id === selected) ?? null;

  const mutation = useMutation({
    mutationFn: (target: { assetId: string } | { assetTag: string } | { softwareId: string } | Record<string, never>) =>
      api.post<WorkOrder>('/work-orders', {
        ...target,
        title,
        description,
        softwareProblemTypeId: ownProblems && showCategory ? softwareProblemTypeId || undefined : undefined,
        category: showCategory && !ownProblems ? category : undefined,
        priority: showPriority ? priority : undefined,
        departmentId: (showDepartment && departmentId) || undefined,
        requesterPersonId: staff ? requester?.id : undefined,
        imageIds: pictures.ids,
      }),
    onSuccess: (workOrder) => {
      invalidateInventory(queryClient);
      toast.success(`Submitted work order #${workOrder.number}`);
      onCreated?.(workOrder);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (pictures.uploading) {
      setError('Wait for the pictures to finish uploading');
    } else if (fixedDevice) {
      mutation.mutate({ assetId: fixedDevice.id });
    } else if (aboutSoftware) {
      if (!selectedSoftware) setError('Choose the software that isn’t working');
      else if (ownProblems && showCategory && !softwareProblemTypeId) setError('Choose what’s going wrong with it');
      else mutation.mutate({ softwareId: selectedSoftware.id });
    } else if (selected === NO_DEVICE) {
      mutation.mutate({});
    } else if (selected === OTHER_DEVICE) {
      if (assetTag.trim()) mutation.mutate({ assetTag: assetTag.trim() });
      else setError('Enter the asset tag from the device’s label');
    } else if (selected) {
      mutation.mutate({ assetId: selected });
    } else {
      setError(staff ? 'Choose a device, or “Not about a specific device”' : 'Choose the device that needs attention');
    }
  };

  const chooseSoftware = (next: string) => {
    setSoftwareId(next);
    // A problem type belongs to one piece of software.
    setSoftwareProblemTypeId('');
  };

  const chooseSubject = (next: Subject) => {
    setSubject(next);
    setSoftwareProblemTypeId('');
    // The type of problem follows, unless someone already picked one.
    if (next === 'software' && category === 'OTHER') setCategory('SOFTWARE');
    if (next === 'device' && category === 'SOFTWARE') setCategory('OTHER');
  };

  const deviceHint =
    staff && !requester
      ? 'Pick who reported it to list their devices, enter an asset tag, or log work that isn’t about a device.'
      : !staff && !loadingDevices && devices.length === 0
        ? 'No devices are assigned to you, so enter the asset tag from the device’s label.'
        : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={staff ? 'New work order' : 'Report a problem'}
      description={fixedDevice ? `${fixedDevice.assetTag} · ${fixedDevice.name}` : undefined}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="work-order-form" loading={mutation.isPending} disabled={pictures.uploading}>
            {pictures.uploading ? 'Uploading pictures…' : 'Submit'}
          </Button>
        </>
      }
    >
      <form id="work-order-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}

        {staff && (
          <Field as="div" label="Reported by" hint="Optional - pick the person this is for, or leave empty if you’re logging it yourself.">
            <PersonPicker
              inline
              value={requester}
              onChange={(person) => {
                setRequester(person);
                setSelection('');
              }}
            />
          </Field>
        )}

        {offersSoftware && (
          <Field as="div" label="What’s it about?">
            <Segmented<Subject>
              value={subject}
              onChange={chooseSubject}
              options={[
                { value: 'device', label: 'Device', icon: <Laptop /> },
                { value: 'software', label: 'Software', icon: <AppWindow /> },
              ]}
            />
          </Field>
        )}

        {aboutSoftware && (
          <Field label="Software" required>
            <Select value={selectedSoftware?.id ?? ''} onChange={(e) => chooseSoftware(e.target.value)}>
              <option value="" disabled>
                Choose the software
              </option>
              {softwareOptions.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.publisher ? `${entry.name} (${entry.publisher})` : entry.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {!fixedDevice && !aboutSoftware && (
          <>
            <Field label="Device" required={!staff} hint={deviceHint}>
              <Select value={selected} onChange={(e) => setSelection(e.target.value)}>
                {selected === '' && (
                  <option value="" disabled>
                    {loadingDevices ? 'Loading devices…' : 'Choose a device'}
                  </option>
                )}
                {groupDevices(devices, ownLabel).map(([label, items]) => (
                  <optgroup key={label} label={label}>
                    {items.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.assetTag} - {item.name}
                        {item.workOrders.length > 0 ? ` (open ticket #${item.workOrders[0].number})` : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
                <option value={OTHER_DEVICE}>Other device - enter its asset tag</option>
                {staff && <option value={NO_DEVICE}>Not about a specific device</option>}
              </Select>
            </Field>
            {selectedDevice && <DevicePreview device={selectedDevice} />}
            {selected === OTHER_DEVICE && (
              <Field label="Asset tag" required hint="It’s printed on the asset label on the device.">
                <Input value={assetTag} onChange={(e) => setAssetTag(e.target.value)} maxLength={64} placeholder="e.g. LA01024" className="font-mono" />
              </Field>
            )}
          </>
        )}

        {/* What the school has already written about this device or software - worth a look before reporting it. */}
        <RelatedArticles
          modelId={aboutSoftware ? null : (fixedDevice?.modelId ?? selectedDevice?.modelId ?? null)}
          softwareId={aboutSoftware ? (selectedSoftware?.id ?? null) : null}
          label={aboutSoftware ? (selectedSoftware?.name ?? null) : (fixedDevice?.model ?? selectedDevice?.model ?? null)}
        />

        <Field label="What’s wrong?" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={150}
            required
            placeholder={aboutSoftware ? 'e.g. It won’t let me sign in' : 'e.g. Screen is cracked'}
          />
        </Field>

        {(showCategory || showPriority) && (
          <div className={cn('grid gap-4', showCategory && showPriority && 'sm:grid-cols-2')}>
            {showCategory && (
              <Field label="Type of problem" required={ownProblems}>
                {/* Software that keeps its own list of problems is reported against that, not the device categories. */}
                {ownProblems ? (
                  <Select value={softwareProblemTypeId} onChange={(e) => setSoftwareProblemTypeId(e.target.value)}>
                    <option value="" disabled>
                      What’s going wrong?
                    </option>
                    {softwareProblems.map((problem) => (
                      <option key={problem.id} value={problem.id}>
                        {problem.name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Select value={category} onChange={(e) => setCategory(e.target.value as WorkOrderCategory)}>
                    {(Object.keys(WORK_ORDER_CATEGORY_LABEL) as WorkOrderCategory[]).map((value) => (
                      <option key={value} value={value}>
                        {WORK_ORDER_CATEGORY_LABEL[value]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
            {showPriority && (
              <Field label="How urgent is it?">
                <Select value={priority} onChange={(e) => setPriority(e.target.value as WorkOrderPriority)}>
                  {PRIORITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </div>
        )}

        {showDepartment && departments.data && (
          <Field label={staff ? 'Department' : 'Which team should handle it?'} hint={staff ? undefined : 'Optional - leave it and the technology team will route it.'}>
            <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
              <option value="">{staff ? 'No department (let workflows route it)' : 'Not sure'}</option>
              {departments.data.map((department) => (
                <option key={department.id} value={department.id}>
                  {indentedName(department)}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label="Details" required hint="When did it start? What have you already tried?">
          <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={5000} required />
        </Field>

        <PicturesField pending={pictures} active={open} />
      </form>
    </Modal>
  );
}

/** Someone a facilities technician logs a request for, from the school's directory. */
function FacilitiesPersonPicker({ value, onChange }: { value: PersonSummary | null; onChange(person: PersonSummary | null): void }) {
  const [query, setQuery] = useState('');
  const search = useDebouncedValue(query.trim(), 250);
  const results = useQuery({
    queryKey: ['facilities', 'people', search],
    queryFn: () => api.get<PersonSummary[]>('/facilities/people', { search }),
    enabled: search.length > 0,
  });

  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-brand-200 bg-brand-50/50 px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-900">{personName(value)}</p>
          {value.email && <p className="truncate text-xs text-slate-500">{value.email}</p>}
        </div>
        <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
          Change
        </Button>
      </div>
    );
  }
  return (
    <Combobox
      inline
      query={query}
      onQueryChange={setQuery}
      loading={results.isFetching}
      items={search ? (results.data ?? []) : []}
      getKey={(person) => person.id}
      placeholder="Search by name or email"
      emptyText="No active people match"
      onSelect={(person) => {
        onChange(person);
        setQuery('');
      }}
      renderItem={(person) => (
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-slate-900">{personName(person)}</p>
          {person.email && <p className="truncate text-xs text-slate-500">{person.email}</p>}
        </div>
      )}
    />
  );
}

function FacilitiesRequestModal({ open, onClose, equipment, staff = false, onCreated }: WorkOrderFormModalProps) {
  const desk = useDesk();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [requester, setRequester] = useState<PersonSummary | null>(null);
  const [place, setPlace] = useState({ buildingId: '', roomId: '' });
  const [locationDetail, setLocationDetail] = useState('');
  const [equipmentId, setEquipmentId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<WorkOrderPriority>('NORMAL');
  const [error, setError] = useState('');
  const pictures = usePendingPictures();
  const resetPictures = pictures.reset;

  useEffect(() => {
    if (!open) return;
    resetPictures();
    setRequester(null);
    setPlace({ buildingId: equipment?.buildingId ?? '', roomId: equipment?.roomId ?? '' });
    setLocationDetail('');
    setEquipmentId(equipment?.id ?? '');
    setDepartmentId('');
    setTitle('');
    setDescription('');
    setPriority('NORMAL');
    setError('');
  }, [open, equipment, resetPictures]);

  const departments = useQuery({
    queryKey: ['departments', 'selectable', desk.service],
    queryFn: () => api.get<DepartmentOption[]>('/my/departments', desk.query()),
    enabled: open,
    staleTime: 60_000,
  });
  const equipmentOptions = useEquipmentOptions(place.buildingId, open && !equipment && !!place.buildingId);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<WorkOrder>(desk.path('/work-orders'), {
        title,
        description,
        priority,
        departmentId: departmentId || undefined,
        buildingId: place.buildingId || undefined,
        roomId: place.roomId || undefined,
        locationDetail: locationDetail.trim() || undefined,
        facilityAssetId: equipmentId || undefined,
        requesterPersonId: staff ? requester?.id : undefined,
        imageIds: pictures.ids,
      }),
    onSuccess: (workOrder) => {
      invalidateInventory(queryClient);
      toast.success(`Sent request #${workOrder.number}${workOrder.assignedTo ? ` to ${workOrder.assignedTo.name}` : ''}`);
      onCreated?.(workOrder);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (!place.buildingId) {
      setError('Choose the building where the work is needed');
      return;
    }
    if (pictures.uploading) {
      setError('Wait for the pictures to finish uploading');
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={staff ? 'New facilities work order' : 'Request facilities work'}
      description={equipment ? equipmentLabel(equipment) : 'Repairs, cleaning, grounds, safety, or anything else the facilities team handles.'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="facilities-request-form" loading={mutation.isPending} disabled={pictures.uploading}>
            {pictures.uploading ? 'Uploading pictures…' : 'Send request'}
          </Button>
        </>
      }
    >
      <form id="facilities-request-form" onSubmit={submit} className="space-y-4">
        {error && <Alert tone="red">{error}</Alert>}

        {staff && (
          <Field label="Requested by" hint="Optional - pick the person this is for, or leave empty if you’re logging it yourself.">
            <FacilitiesPersonPicker value={requester} onChange={setRequester} />
          </Field>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <PlaceFields
            requireBuilding
            buildingId={place.buildingId}
            roomId={place.roomId}
            onChange={(next) => {
              setPlace(next);
              if (!equipment && next.buildingId !== place.buildingId) setEquipmentId('');
            }}
          />
        </div>
        <Field label="Where exactly?" hint="Optional - e.g. north stairwell, sink by the window, parking lot C">
          <Input value={locationDetail} onChange={(e) => setLocationDetail(e.target.value)} maxLength={200} />
        </Field>

        {!equipment && !!equipmentOptions.data?.length && (
          <Field label="Equipment" hint="Optional - if you know which unit or system it is">
            <Select value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)}>
              <option value="">Not sure, or none</option>
              {equipmentOptions.data.map((option) => (
                <option key={option.id} value={option.id}>
                  {equipmentLabel(option)}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label="What needs attention?" required>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={150} required placeholder="e.g. Sink is leaking" />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          {!!departments.data?.length && (
            <Field label={staff ? 'Category' : 'What kind of work is it?'} hint={staff ? undefined : 'Optional - it helps get the request to the right crew.'}>
              <Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                <option value="">{staff ? 'No category' : 'Not sure'}</option>
                {departments.data.map((department) => (
                  <option key={department.id} value={department.id}>
                    {indentedName(department)}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label="How urgent is it?">
            <Select value={priority} onChange={(e) => setPriority(e.target.value as WorkOrderPriority)}>
              {FACILITIES_PRIORITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Details" required hint="What’s happening, and since when? Anything the team should know before they arrive?">
          <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={5000} required />
        </Field>

        <PicturesField pending={pictures} active={open} />
      </form>
    </Modal>
  );
}
